import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { Router } from '@angular/router';

import { switchMap } from 'rxjs';
import { UserService } from '@ipg/ngx-frontend-toolset';
import { BaseDialog } from '../../../../components/dialog/base-dialog';
import { MediaReference, MediaReferenceGroup } from '../../../../models/media.model';
import { User } from '../../../../models/user.model';
import { UnitService } from '../../../units/services/unit.service';

export interface ReferenceGroup {
  unitId: string | null;
  unitLabel: string;
  references: MediaReference[];
  totalCount: number;
}

export interface MediaSubGroup {
  filename: string;
  references: MediaReference[];
}

export interface TypeSubGroup {
  type: string;
  label: string;
  icon: string;
  references: MediaReference[];
}

export interface MediaReferencesDialogData {
  /** Pre-grouped references by unit from the backend */
  referenceGroups?: ReferenceGroup[];
  /** Per-media grouped references (used for bulk unshare) */
  mediaGroups?: MediaReferenceGroup[];
  /** 'unshare' shows a "cannot unshare" message; 'delete' shows cannot delete */
  mode: 'unshare' | 'delete';
  /** Number of media items that can still be deleted (no refs) */
  deletableCount?: number;
}

@Component({
  selector: 'app-media-references-dialog',
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatButton,
    MatIcon,
    MatIconButton,
  ],
  templateUrl: './media-references-dialog.component.html',
  styleUrl: './media-references-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaReferencesDialogComponent extends BaseDialog<MediaReferencesDialogData> {
  protected readonly dialogRef = inject(MatDialogRef<MediaReferencesDialogComponent>);
  private readonly _router = inject(Router);
  private readonly _unitService = inject(UnitService);
  private readonly _userService = inject<UserService<User>>(UserService);

  protected readonly isMediaGrouped = !!this.data.mediaGroups?.length;
  protected readonly groupedReferences: ReferenceGroup[] =
    this.data.referenceGroups ?? [];

  /** Whether multiple distinct media items are present in the references */
  protected readonly hasMultipleMedia = this._detectMultipleMedia();

  private readonly _collapsedTypeGroups = new Set<string>();

  protected toggleTypeGroup(groupUnitId: string | null, type: string): void {
    const key = `${groupUnitId}::${type}`;
    if (this._collapsedTypeGroups.has(key)) {
      this._collapsedTypeGroups.delete(key);
    } else {
      this._collapsedTypeGroups.add(key);
    }
  }

  protected isTypeGroupExpanded(groupUnitId: string | null, type: string): boolean {
    return !this._collapsedTypeGroups.has(`${groupUnitId}::${type}`);
  }

  protected get titleText(): string {
    return this.data.mode === 'unshare'
      ? 'Kan niet stoppen met delen'
      : 'Media is in gebruik';
  }

  protected get descriptionText(): string {
    if (this.isMediaGrouped) {
      return this.data.mode === 'unshare'
        ? 'Een of meer media items kunnen niet worden stopgezet met delen omdat ze nog in gebruik zijn door andere units. Verwijder eerst de koppeling(en) hieronder.'
        : 'Een of meer media items kunnen niet worden verwijderd omdat ze nog in gebruik zijn. Verwijder eerst de koppeling(en) hieronder.';
    }

    const count = this.groupedReferences.reduce((sum, g) => sum + g.totalCount, 0);

    if (this.data.mode === 'unshare') {
      return `Deze media kan niet worden stopgezet met delen omdat ${count === 1 ? 'het' : 'ze'} nog in gebruik ${count === 1 ? 'is' : 'zijn'} door andere units. Verwijder eerst de koppeling(en) hieronder.`;
    }

    return `Deze media kan niet worden verwijderd omdat ${count === 1 ? 'het' : 'ze'} nog in gebruik ${count === 1 ? 'is' : 'zijn'}. Verwijder eerst de koppeling(en) hieronder.`;
  }

  protected typeLabel(type: string): string {
    const map: Record<string, string> = {
      scm: 'Signalering',
      interaction: 'Interactie',
      interactions: 'Interactie',
    };

    return map[type] ?? type;
  }

  protected typeLabelPlural(type: string): string {
    const map: Record<string, string> = {
      scm: 'Signaleringen',
      interaction: 'Interacties',
      interactions: 'Interacties',
    };

    return map[type] ?? type;
  }

  protected getMediaGroupTypeSubGroups(
    mediaGroup: MediaReferenceGroup
  ): TypeSubGroup[] {
    const map = new Map<string, MediaReference[]>();

    for (const ref of mediaGroup.references) {
      const key = ref.type || '';
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key)!.push(ref);
    }

    return Array.from(map.entries()).map(([type, references]) => ({
      type,
      label: this.typeLabel(type),
      icon: type === 'scm' ? 'leak_add' : 'history',
      references,
    }));
  }

  protected getMediaSubGroups(group: ReferenceGroup): MediaSubGroup[] {
    const map = new Map<string, MediaReference[]>();

    for (const ref of group.references) {
      const key = ref.filename || '';
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key)!.push(ref);
    }

    return Array.from(map.entries()).map(([filename, references]) => ({
      filename,
      references,
    }));
  }

  protected getTypeSubGroups(group: ReferenceGroup): TypeSubGroup[] {
    const map = new Map<string, MediaReference[]>();

    for (const ref of group.references) {
      const key = ref.type || '';
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key)!.push(ref);
    }

    return Array.from(map.entries()).map(([type, references]) => ({
      type,
      label: this.typeLabel(type),
      icon: type === 'scm' ? 'leak_add' : 'history',
      references,
    }));
  }

  protected navigateToReference(ref: MediaReference): void {
    const currentUnitId = this._userService.data()?.unitId;

    this.dialogRef.close();

    if (ref.unitId && ref.unitId !== currentUnitId) {
      // Switch to the reference's unit, then navigate
      this._unitService
        .activate(ref.unitId)
        .pipe(switchMap(() => this._userService.fetch()))
        .subscribe(() => {
          this._router.navigateByUrl(ref.path);
        });
    } else {
      this._router.navigateByUrl(ref.path);
    }
  }

  private _detectMultipleMedia(): boolean {
    const filenames = new Set<string>();

    for (const group of this.groupedReferences) {
      for (const ref of group.references) {
        filenames.add(ref.filename);
      }
    }

    return filenames.size > 1;
  }
}
